import { useState } from "react";
import { collection, addDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../services/firebase/config";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
  Container, Paper, Typography, TextField, Button, Box,
  Grid, CircularProgress, FormControl, InputLabel, Select, MenuItem
} from "@mui/material";

function ItemManagement() {
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useSelector((state) => state.auth.isAdmin);
  const [loading, setLoading] = useState(false);
  const [itemData, setItemData] = useState({
    name: "",
    price: "",
    quantity: "",
    description: "",
    category: "furniture", // furniture, outdoor, appliance, decoration
    location: "indoor", // indoor or outdoor
    status: "available"
  });
  const [itemImage, setItemImage] = useState(null);

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setItemData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 5 * 1024 * 1024) {
      setItemImage(file);
    } else {
      alert("Please select an image under 5MB");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let imageUrl = "";
      if (itemImage) {
        const storageRef = ref(storage, `item-images/${Date.now()}_${itemImage.name}`);
        await uploadBytes(storageRef, itemImage);
        imageUrl = await getDownloadURL(storageRef);
      }

      await addDoc(collection(db, "items"), {
        ...itemData,
        image: imageUrl,
        createdAt: new Date(),
        addedBy: user.uid,
        price: Number(itemData.price),
        quantity: Number(itemData.quantity),
        purchasedBy: []
      });

      alert("Item added successfully!");
      setItemData({
        name: "",
        price: "",
        quantity: "",
        description: "",
        category: "furniture",
        location: "indoor",
        status: "available"
      });
      setItemImage(null);
    } catch (error) {
      console.error("Error adding item:", error);
      alert("Error adding item: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Add New Item</Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Item Name"
                name="name"
                value={itemData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price"
                name="price"
                type="number"
                value={itemData.price}
                onChange={handleInputChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity Available"
                name="quantity"
                type="number"
                value={itemData.quantity}
                onChange={handleInputChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={4}
                value={itemData.description}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={itemData.category}
                  label="Category"
                  onChange={handleInputChange}
                >
                  <MenuItem value="furniture">Furniture</MenuItem>
                  <MenuItem value="outdoor">Outdoor</MenuItem>
                  <MenuItem value="appliance">Appliance</MenuItem>
                  <MenuItem value="decoration">Decoration</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  name="location"
                  value={itemData.location}
                  label="Location"
                  onChange={handleInputChange}
                >
                  <MenuItem value="indoor">Indoor</MenuItem>
                  <MenuItem value="outdoor">Outdoor</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" component="label" fullWidth>
                Upload Item Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
              {itemImage && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Selected: {itemImage.name}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "Add Item"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
}

export default ItemManagement;